import { z } from "zod";
import { normalizeUSPhoneNumber } from "@/lib/utils/phone/phone-utils";
import type { MedplumGender, Member } from "@/models/member/Member";

/** Fullscript "Biological sex" values on `POST/PATCH /clinic/patients`. */
type FullscriptGender = "male" | "female" | "x";

/** Optional demographic fields that Fullscript accepts on patient create and update. */
export interface FullscriptPatientDemographics {
  /** yyyy-mm-dd */
  date_of_birth?: string;
  gender?: FullscriptGender;
  /** E.164, e.g. "+12223334444" */
  mobile_number?: string;
}

type DemographicField = keyof FullscriptPatientDemographics;

// `Patient.gender` is the sex the app already uses for lab ordering (Junction reads it the same way,
// see `RequiredSexAtBirthSchema`). The app stores no us-core-birthsex extension, so there is no
// better source for Fullscript's "Biological sex". "unknown" gives Fullscript nothing to act on.
const FULLSCRIPT_GENDER_BY_MEDPLUM_GENDER: Record<MedplumGender, FullscriptGender | undefined> = {
  male: "male",
  female: "female",
  other: "x",
  unknown: undefined,
};

const FullscriptPatientDemographicsSchema = z.object({
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  date_of_birth: z.string().nullish(),
  gender: z.string().nullish(),
  mobile_number: z.string().nullish(),
});
type FullscriptPatientFields = z.infer<typeof FullscriptPatientDemographicsSchema>;

/**
 * Builds the Fullscript demographic fields for a member. A field is omitted (never null or "")
 * when the member has no value or the value cannot become a valid Fullscript value. Without these
 * fields, Fullscript lab availability warns `patient_missing_date_of_birth`, `patient_missing_sex`
 * and `patient_missing_sms_number`. The patient then enters them again at checkout.
 */
export function buildFullscriptPatientDemographics(member: Member): FullscriptPatientDemographics {
  const demographics: FullscriptPatientDemographics = {};

  // partial FHIR dates (yyyy, yyyy-mm) are not a date of birth for Fullscript
  const birthDate = member.dateOfBirth;
  if (birthDate && z.iso.date().safeParse(birthDate).success)
    demographics.date_of_birth = birthDate;

  const gender = member.gender && FULLSCRIPT_GENDER_BY_MEDPLUM_GENDER[member.gender];
  if (gender) demographics.gender = gender;

  // Member keeps one contact phone w/o a `use`; Fullscript texts it, so send only a valid US number
  const usPhoneDigits = normalizeUSPhoneNumber(member.phone);
  if (usPhoneDigits) demographics.mobile_number = `+1${usPhoneDigits}`;

  return demographics;
}

/**
 * Returns the demographic fields that the Fullscript patient does not have yet. A field counts as
 * missing only when the GET response has its key with a null or "" value. Fullscript does not
 * document these keys on GET, so an absent key means "unknown" and the field is skipped. A value
 * that the patient or the clinic set in Fullscript is never in the result.
 */
export function pickMissingFullscriptDemographics({
  demographics,
  fullscriptPatient,
}: {
  demographics: FullscriptPatientDemographics;
  fullscriptPatient: unknown;
}): FullscriptPatientDemographics {
  const parsed = FullscriptPatientDemographicsSchema.safeParse(fullscriptPatient);
  // unreadable response - send nothing rather than risk an overwrite
  if (!parsed.success) return {};

  const missing: FullscriptPatientDemographics = {};
  for (const field of Object.keys(demographics) as DemographicField[]) {
    // an absent key reads as undefined and is skipped, same as a set value
    const remoteValue = parsed.data[field];
    if (remoteValue !== null && remoteValue !== "") continue;
    Object.assign(missing, { [field]: demographics[field] });
  }
  return missing;
}

/**
 * Which demographic keys the Fullscript GET response carries. Fullscript does not document them on
 * GET, so this is logged to tell "key absent" from "already set". Presence only, never values.
 */
export function readFullscriptDemographicKeys(fullscriptPatient: unknown): {
  hasDobKey: boolean;
  hasGenderKey: boolean;
  hasMobileKey: boolean;
} {
  const parsed = FullscriptPatientDemographicsSchema.safeParse(fullscriptPatient);
  const data: FullscriptPatientFields = parsed.success ? parsed.data : {};
  return {
    hasDobKey: "date_of_birth" in data,
    hasGenderKey: "gender" in data,
    hasMobileKey: "mobile_number" in data,
  };
}

const NAME_PUNCTUATION = /[.,']/g;
const NAME_WHITESPACE = /\s+/;
const GIVEN_NAME_SEPARATORS = /[\s-]+/;
// combining marks that NFD splits off: "José" -> "Jose"
const COMBINING_MARKS = /\p{M}/gu;
const FAMILY_NAME_SUFFIXES = ["jr", "sr", "ii", "iii", "iv"];

/** The name fields that the compare reads. `Member` satisfies this shape. */
export interface FullscriptPersonName {
  firstName?: string;
  lastName?: string;
}

// toLowerCase, not toLocaleLowerCase: the folded name is a match key that gates sending PHI, so it
// must fold the same on every host (a Turkish locale folds I/İ differently)
function foldName(name: string | null | undefined): string {
  return (name ?? "")
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .replace(NAME_PUNCTUATION, "")
    .toLowerCase();
}

function toNameTokens(name: string | null | undefined): string[] {
  return foldName(name).split(NAME_WHITESPACE).filter(Boolean);
}

/**
 * True when one given name starts with the other, token by token. "Mary Ann" in Fullscript is the
 * member "Mary", and "Anne-Marie" is "Anne Marie". "Jean-Luc" is not "Jean-Pierre".
 */
function givenNamesMatch(a: string | null | undefined, b: string | null | undefined): boolean {
  const aTokens = foldName(a).split(GIVEN_NAME_SEPARATORS).filter(Boolean);
  const bTokens = foldName(b).split(GIVEN_NAME_SEPARATORS).filter(Boolean);
  if (!aTokens.length || !bTokens.length) return false;
  const sharedLength = Math.min(aTokens.length, bTokens.length);
  return aTokens.slice(0, sharedLength).every((token, index) => token === bTokens[index]);
}

// "Doe Jr." -> "doe"; a hyphenated name stays whole, so "Smith-Jones" never equals "Smith"
function normalizeFamilyName(name: string | null | undefined): string | undefined {
  const tokens = toNameTokens(name);
  while (tokens.length > 1 && FAMILY_NAME_SUFFIXES.includes(tokens[tokens.length - 1]))
    tokens.pop();
  return tokens.join(" ") || undefined;
}

// "Doe Jr." -> "jr"; "Doe" -> "" - the suffixes that normalizeFamilyName strips, in order
function readFamilyNameSuffix(name: string | null | undefined): string {
  const tokens = toNameTokens(name);
  const suffixes: string[] = [];
  while (tokens.length > 1 && FAMILY_NAME_SUFFIXES.includes(tokens[tokens.length - 1]))
    suffixes.unshift(tokens.pop() as string);
  return suffixes.join(" ");
}

/**
 * True when the names are the same after folding case, accents, and punctuation, with no other
 * leniency: no dropped family-name suffix and no extra given name. "John Smith" is not
 * "John Smith Jr", and "Mary" is not "Mary Ann". A missing name on either side is false.
 */
export function isExactFullscriptPersonName({
  member,
  fullscriptPatient,
}: {
  member: FullscriptPersonName;
  fullscriptPatient: unknown;
}): boolean {
  const parsed = FullscriptPatientDemographicsSchema.safeParse(fullscriptPatient);
  const data: FullscriptPatientFields = parsed.success ? parsed.data : {};
  const memberGiven = foldName(member.firstName).split(GIVEN_NAME_SEPARATORS).filter(Boolean);
  const patientGiven = foldName(data.first_name).split(GIVEN_NAME_SEPARATORS).filter(Boolean);
  const memberFamily = toNameTokens(member.lastName).join(" ");
  const patientFamily = toNameTokens(data.last_name).join(" ");
  return (
    !!memberGiven.length &&
    memberGiven.join(" ") === patientGiven.join(" ") &&
    !!memberFamily.length &&
    memberFamily === patientFamily
  );
}

/**
 * Compares the member's name with the Fullscript patient's name. A patient linked only through a
 * shared email can be a different person, so a caller must not act on that link (send demographics,
 * link the member) without a match. Names are human text: the compare ignores case, accents,
 * punctuation, a hyphen or space between given names, extra given names on one side, and a
 * family-name suffix. A name missing on either side is a mismatch (fail closed).
 */
export function compareFullscriptPersonName({
  member,
  fullscriptPatient,
}: {
  member: FullscriptPersonName;
  fullscriptPatient: unknown;
}): { givenMatches: boolean; familyMatches: boolean } {
  const parsed = FullscriptPatientDemographicsSchema.safeParse(fullscriptPatient);
  const data: FullscriptPatientFields = parsed.success ? parsed.data : {};
  const family = normalizeFamilyName(member.lastName);
  return {
    givenMatches: givenNamesMatch(member.firstName, data.first_name),
    familyMatches: !!family && family === normalizeFamilyName(data.last_name),
  };
}

// partial FHIR dates (yyyy, yyyy-mm) cannot prove identity - treat them as missing
function toFullDate(value: string | null | undefined): string | undefined {
  return value && z.iso.date().safeParse(value).success ? value : undefined;
}

/**
 * Decides whether the Fullscript patient is the same person as the member. A shared family email
 * is not proof of identity: a guardian's Fullscript patient can carry the email of a child's chart.
 * When both sides have a full date of birth, the dates must be equal. Otherwise the given name and
 * the family name must both match, and so must a family-name suffix (Jr, Sr, II): a parent and a
 * child often share a name. Anything else is not the same person (fail closed).
 *
 * @returns `basis` names the evidence that decided, for logs. It never holds a value.
 */
export function compareFullscriptPersonIdentity({
  member,
  fullscriptPatient,
}: {
  member: Member;
  fullscriptPatient: unknown;
}): { isSamePerson: boolean; basis: "date-of-birth" | "name" } {
  const parsed = FullscriptPatientDemographicsSchema.safeParse(fullscriptPatient);
  const memberBirthDate = toFullDate(member.dateOfBirth);
  const fullscriptBirthDate = parsed.success ? toFullDate(parsed.data.date_of_birth) : undefined;
  if (memberBirthDate && fullscriptBirthDate)
    return { isSamePerson: memberBirthDate === fullscriptBirthDate, basis: "date-of-birth" };

  const { givenMatches, familyMatches } = compareFullscriptPersonName({
    member,
    fullscriptPatient,
  });
  // "Pat Rivera" and "Pat Rivera Jr." share a household email but are a parent and a child
  const fullscriptLastName = parsed.success ? parsed.data.last_name : undefined;
  const suffixMatches =
    readFamilyNameSuffix(member.lastName) === readFamilyNameSuffix(fullscriptLastName);
  return { isSamePerson: givenMatches && familyMatches && suffixMatches, basis: "name" };
}
